import React, {useMemo, useEffect, useState} from 'react';
import {Box, Checkbox, Grid, Stack, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {t} from 'i18next';

import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ProjectServerPath from './ProjectServerPath';
import ProjectAnalystics from './ProjectAnalytics';
import {ISelectItem} from '../../../shared/types';
import {
  useGetProjectList,
  useGetProjectTemplates,
} from '../../../hooks/Projects/useProjects';
import {KeyValue} from '../../common/arr-transfer-list/ArrTransferList';

import './CreateProjectPage.scss';
import './GeneralTab.scss';
import {validateFolderPath} from './projectUtility';

import {
  setDeleteProject,
  setIsActiveProject,
  setSelectedProject,
  setFolderPaths,
  setServerSelected,
  setToggles,
  ToggleItem,
  setProjectDBServerId,
  setIsRedactionPropagationActive,
} from '../../../redux/slice/projectFormSlice';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/active-unchecked.svg';
import {ReactComponent as InactiveIcon} from '../../../../src/assets/images/icon-unchecked-inactive.svg';

const GeneralTab: React.FC<{
  handleActiveProjectChange?: (isActive: boolean) => void;

  onValidationChange?: (isValid: boolean) => void;
}> = ({handleActiveProjectChange, onValidationChange}) => {
  const dispatch = useDispatch();
  const {
    isActiveProject,
    deleteProject,
    selectedProject,
    projectFolderPath,
    ingestionFolderPath,
    productionFolderPath,
    projectServerSelected,
    loadServerSelected,
    productionServerSelected,
    toggles,
    type,
    selectedClientId,
  } = useSelector((state: RootState) => state.projectForm);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const {data: projectTemplateData} = useGetProjectTemplates(
    selectedProject?.key as string,
    type as string
  );

  useEffect(() => {
    if (projectTemplateData) {
      dispatch(
        setIsRedactionPropagationActive(
          projectTemplateData?.isRedactionPropagated
        )
      );
    }
  }, [projectTemplateData]);

  useEffect(() => {
    if (
      (selectedProject && toggles.length === 0 && type != 'edit') ||
      !selectedProject
    ) {
      dispatch(
        setToggles([
          {key: 'users', label: 'Users', checked: false},
          {key: 'assignments', label: 'Assignments', checked: false},
          {key: 'savedSearches', label: 'Saved Searches', checked: false},
          {
            key: 'assignedReviewers',
            label: 'Assigned Reviewers',
            checked: false,
            parent: 'assignments',
          },
          {key: 'customReports', label: 'Custom Reports', checked: false},
        ])
      );
    }
  }, [selectedProject, toggles.length, dispatch]);

  const domainData = useSelector((state: RootState) => state.domainData.data);
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  useEffect(() => {
    const isValid =
      validateFolderPath(projectFolderPath) &&
      validateFolderPath(ingestionFolderPath) &&
      validateFolderPath(productionFolderPath) &&
      projectServerSelected.length > 0 &&
      loadServerSelected.length > 0 &&
      productionServerSelected.length > 0;

    onValidationChange?.(isValid);
  }, [
    projectFolderPath,
    ingestionFolderPath,
    productionFolderPath,
    projectServerSelected,
    loadServerSelected,
    productionServerSelected,
    onValidationChange,
  ]);

  const projectServerList: KeyValue[] = useMemo(() => {
    return (
      domainData?.projectServers?.map((item) => ({
        id: Number(item.value),
        label: item.text,
      })) ?? []
    );
  }, [domainData]);

  const loadServerList: KeyValue[] = useMemo(() => {
    return (
      domainData?.loadServers?.map((item) => ({
        id: Number(item.value),
        label: item.text,
      })) ?? []
    );
  }, [domainData]);

  const productionServerList: KeyValue[] = useMemo(() => {
    return (
      domainData?.productionServers?.map((item) => ({
        id: Number(item.value),
        label: item.text,
      })) ?? []
    );
  }, [domainData]);

  useEffect(() => {
    if (!domainData) return;

    if (projectServerSelected.length === 0) {
      const unselectedServers =
        domainData.projectServers?.filter((item) => !item.selected) ?? [];
      const lastIndex = unselectedServers.length - 1;

      if (lastIndex >= 0) {
        const latest = unselectedServers[lastIndex];
        dispatch(
          setServerSelected({
            type: 'project',
            selected: [{id: Number(latest.value), label: latest.text}],
          })
        );
      }
    }

    if (loadServerSelected.length === 0) {
      const initialLoad =
        domainData.loadServers
          ?.filter((item) => !item.selected)
          .map((item) => ({id: Number(item.value), label: item.text})) ?? [];
      dispatch(setServerSelected({type: 'load', selected: initialLoad}));
    }

    if (productionServerSelected.length === 0) {
      const initialProd =
        domainData.productionServers
          ?.filter((item) => !item.selected)
          .map((item) => ({id: Number(item.value), label: item.text})) ?? [];
      dispatch(setServerSelected({type: 'production', selected: initialProd}));
    }
    if (domainData.projectDBServer) {
      const key = Number(Object.keys(domainData.projectDBServer)[0]);
      dispatch(setProjectDBServerId(key));
    }
  }, [domainData, dispatch]);

  const handleTransferChange = (
    type: 'project' | 'load' | 'production',
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    const isChecked = event.target.checked;

    const selectedList =
      type === 'project'
        ? projectServerSelected
        : type === 'load'
          ? loadServerSelected
          : productionServerSelected;

    let updatedList;

    if (type === 'project') {
      updatedList = [field];
    } else if (isChecked) {
      updatedList = [...selectedList, field];
    } else {
      updatedList = selectedList.filter((item) => item.id !== field.id);
    }

    dispatch(setServerSelected({type, selected: updatedList}));
  };

  const {data} = useGetProjectList(2, selectedClientId ?? 0, type ?? '');

  const dropdownItems: ISelectItem[] = useMemo(() => {
    return (
      data?.folder?.map((item) => ({
        key: item.key,
        displayValue: item.value,
      })) ?? []
    );
  }, [data]);

  const handleToggleChange = (key: string) => {
    const currentChecked = toggles.find((t) => t.key === key)?.checked ?? false;

    const updatedToggles = toggles.map((item) => {
      if (item.key === key) {
        return {...item, checked: !currentChecked};
      }

      if (item.parent === key && currentChecked) {
        return {...item, checked: false};
      }
      return item;
    });

    dispatch(setToggles(updatedToggles));
  };

  const RenderToggleColumns = () => {
    const parentOptions = toggles.filter((opt) => !opt.parent);
    const leftColumn = parentOptions.filter((_, i) => i % 2 === 0);
    const rightColumn = parentOptions.filter((_, i) => i % 2 !== 0);

    const renderColumn = (columnOptions: ToggleItem[]) => (
      <Stack spacing={0}>
        {columnOptions.map((parent, index) => (
          <Box key={parent.key}>
            <Box display="flex" alignItems="center" gap={1}>
              <ArrToggle
                disabled={parent.disabled}
                checked={parent.checked}
                onChange={() => handleToggleChange(parent.key)}
                dataTestId={`column-options-toggle-${index}-${parent.key}`}
              />
              <span
                className="typo-text body4_size"
                data-testid={`general-tab-parent-label-${index}-${parent.key}`}
              >
                {parent.label}
              </span>
            </Box>

            {parent.checked &&
              toggles[0].checked &&
              toggles
                .filter((child) => child.parent === parent.key)
                .map((child, childIndex) => (
                  <Box
                    key={child.key}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    ml={4}
                    mt={0}
                  >
                    <ArrToggle
                      disabled={child.disabled}
                      checked={child.checked}
                      onChange={() => handleToggleChange(child.key)}
                      dataTestId="general-toggle"
                    />
                    <span
                      className="typo-text body4_size"
                      data-testid={`general-tab-child-label-${index}-${childIndex}`}
                    >
                      {child.label}
                    </span>
                  </Box>
                ))}
          </Box>
        ))}
      </Stack>
    );

    return (
      <Grid container spacing={4} mt={3}>
        <Grid>{renderColumn(leftColumn)}</Grid>
        <Grid>{renderColumn(rightColumn)}</Grid>
      </Grid>
    );
  };

  const confirmDeleteModal = () => {
    setConfirmModal(false);
    dispatch(setDeleteProject(true));
  };

  return (
    <Box className="general-tab">
      <Box className="general-tab-container">
        <Box className="general-tab-container-header">
          <Typography
            className="typo-text subhead3_size"
            data-testid={`general-tab-copy-existing-project`}
          >
            {t('projectManagement.copyExistingProject')}
          </Typography>

          <Box className="toggle-project-data">
            <ArrToggle
              checked={isActiveProject}
              onChange={(e, checked) => {
                dispatch(setIsActiveProject(checked));
                handleActiveProjectChange?.(checked);
              }}
              dataTestId="project-data-toggle"
            />
            <Typography
              className="typo-text body2_size"
              data-testid={`general-tab-project-active`}
            >
              {t('projectManagement.createProjectModule.projectActive')}
            </Typography>

            {type === 'edit' && roleId === -1 && (
              <Box display="flex" alignItems="center">
                <Checkbox
                  data-testid="create-project-general-delete-project-checkbox"
                  checked={!isActiveProject && deleteProject}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    if (checked) {
                      if (!isActiveProject) {
                        setConfirmModal(true);
                        return;
                      }

                      return;
                    }

                    dispatch(setDeleteProject(false));
                  }}
                  disabled={isActiveProject}
                  icon={isActiveProject ? <InactiveIcon /> : <UnCheckedIcon />}
                />
                <Typography
                  className={`typo-text body2_size ${isActiveProject && 'delete-text-inactive'}`}
                  data-testid={`general-tab-delete-project`}
                >
                  {t('projectManagement.createProjectModule.deleteProject')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="existing-project">
        <div
          className="existing-project-dropdown"
          data-testid={`general-tab-div`}
        >
          <ArrSingleSelectDropdown
            dropdownLabel="Select an Existing Project"
            dropdownItems={dropdownItems}
            selectedValues={selectedProject}
            onSelectionChange={(val) => dispatch(setSelectedProject(val))}
            dropdownItemKey="key"
            dropdownItemDisplay="displayValue"
            disabled={type === 'edit'}
            searchable
            showNoResultsText
            clearSelection
            dataTestId="asd-existing-project"
          />
        </div>
        <Typography
          className="typo-text body4_size existing-project-text"
          data-testid={`general-tab-existing-project-text`}
        >
          {t('projectManagement.existingProjectText')}
        </Typography>
      </Box>
      {selectedProject && type != 'edit' && (
        <Box className="toggle-section-handler">
          <Typography
            className="typo-text subhead5_size"
            data-testid={`general-tab-toggle-section-title`}
          >
            {t('projectManagement.toggleSectionTitle')}
          </Typography>
          {RenderToggleColumns()}
        </Box>
      )}
      <Box className="project-workspace-container">
        <div
          className="project-workspace-inner-container"
          data-testid={`general-tab-workspace`}
        >
          <Typography
            className="typo-text subhead3_size workspace-title"
            data-testid={`general-tab-workspace-text`}
          >
            {t('projectManagement.workspace')}
          </Typography>
        </div>
      </Box>
      <Box className="project-main-container">
        <ProjectServerPath
          title="Project Server Path"
          folderValue={projectFolderPath}
          availableList={projectServerList}
          selectedList={projectServerSelected}
          placeholder="Enter project folder path"
          label="Project Folder"
          id="project-folder-path"
          required
          onChange={(e, item) => handleTransferChange('project', e, item)}
          onTextFieldChange={(e) =>
            dispatch(setFolderPaths({type: 'project', path: e.target.value}))
          }
          validationFn={(value) => validateFolderPath(value, 'project')}
          type={type}
          role={roleId}
        />

        <ProjectServerPath
          title="Ingestion Server Path"
          folderValue={ingestionFolderPath}
          availableList={loadServerList}
          selectedList={loadServerSelected}
          onChange={(e, item) => handleTransferChange('load', e, item)}
          placeholder="Enter ingestion folder"
          label="Ingestion Folder"
          id="ingestion-path"
          required
          onTextFieldChange={(e) =>
            dispatch(setFolderPaths({type: 'ingestion', path: e.target.value}))
          }
          validationFn={(value) => validateFolderPath(value, 'ingestion')}
          type={type}
          role={roleId}
        />

        <ProjectServerPath
          title="Production Server Path"
          folderValue={productionFolderPath}
          availableList={productionServerList}
          selectedList={productionServerSelected}
          onChange={(e, item) => handleTransferChange('production', e, item)}
          placeholder="Enter production folder"
          label="Production Folder"
          id="production-path"
          required
          onTextFieldChange={(e) =>
            dispatch(setFolderPaths({type: 'production', path: e.target.value}))
          }
          validationFn={(value) => validateFolderPath(value, 'production')}
          type={type}
          role={roleId}
        />

        <Box>
          <ProjectAnalystics />
        </Box>
      </Box>
      {confirmModal && (
        <ArrModal
          showModal={confirmModal}
          showClose={true}
          onClose={() => setConfirmModal(false)}
          headerTitle={t('userManagement.deleteUser.justToConfirm')}
          title={t('projectManagement.editProject.deleteModalTitle')}
          description={t('projectManagement.editProject.deleteDescription')}
          dataTestId="generaltab-confirm-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setConfirmModal(false),
              variant: 'secondary',
              dataTestId: 'gcm-cancel-btn',
            },
            {
              id: '2',
              label: t('common.deleteIt'),
              onClick: () => confirmDeleteModal(),
              variant: 'primary',
              dataTestId: 'gcm-delete-btn',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default GeneralTab;
